// combatpro/treasure/MonsterTreasureModal.js
//
// Fast per-monster Treasure modal, opened from the shared Active Monster row. It
// operates on ONE monster instance (props.index) and reads/writes that monster's
// treasure through CombatContext, so the state is server-backed (rides the encounter
// document) and per-instance - two identical monsters keep separate loot. Reopening
// shows the SAME stored result; it never silently rerolls (generation only on an
// explicit button). Generation is monster-aware via deriveMonsterContext. The full
// Treasure page reads the identical store. Touch-friendly; scrolls internally.
//
// No optional chaining, no em dash.

import React, { useContext, useState } from 'react';
import { CombatContext } from '../contexts/CombatContext';
import { useTreasureCatalogue } from './useTreasureCatalogue';
import { deriveMonsterContext } from './treasureMonster';
import { generateTreasure } from './treasureEngine';
import { withUids, removeItem, upsertItem, rerollItem } from './treasureResultOps';
import { deriveTabName } from '../monsters/monsterEngine';
import { WEALTH_TIERS, QUANTITIES, CATEGORIES } from './treasureVocab';
import { categoryLabel } from './treasureFormat';
import TreasureResult from './TreasureResult';

var QUANT = ['sparse', 'normal', 'generous', 'huge'];

function MonsterTreasureModal(props) {
  var index = props.index;
  const ctx = useContext(CombatContext);
  const cat = useTreasureCatalogue();
  const [wealth, setWealth] = useState('standard');
  const [quantity, setQuantity] = useState('normal');
  const [editing, setEditing] = useState(null); // {uid?, name, category, qty, unitValue}

  if (!props.isOpen) { return null; }
  var state = ctx.state || {};
  var monster = (state.monsters || [])[index];
  if (!monster) { return null; }
  var treasure = monster.treasure || null;
  var result = treasure ? treasure.result : null;
  var status = treasure ? treasure.status : 'unsearched';
  var mctx = deriveMonsterContext(monster);
  var catalogue = cat.catalogue;
  var ready = catalogue && catalogue.valid;

  function request() {
    return { mode: 'individual', creatureTags: mctx.creatureTags, level: mctx.level, wealth: wealth, quantity: quantity };
  }
  function persist(patch) {
    ctx.setMonsterTreasure(index, Object.assign({ status: status || 'searched', result: result, locked: treasure ? treasure.locked : false }, patch));
  }
  function doGenerate() {
    if (!ready) { return; }
    var r = withUids(generateTreasure(catalogue, Object.assign({}, request(), { seed: Date.now() })));
    ctx.setMonsterTreasure(index, { status: 'searched', result: r, locked: false });
    setEditing(null);
  }
  function onRemoveItem(it) { persist({ result: removeItem(result, it.uid) }); }
  function onRerollItem(it) { persist({ result: rerollItem(catalogue, request(), result, it.uid) }); }
  function onEditItem(it) { setEditing({ uid: it.uid, name: it.name, category: it.category, qty: it.qty, unitValue: it.unitValue }); }
  function onAddNew() { setEditing({ name: '', category: 'curios', qty: 1, unitValue: 0 }); }
  function submitEdit() {
    if (!editing || !editing.name) { setEditing(null); return; }
    var base = result || { coins: { counts: {}, value: 0 }, items: [], totalValue: 0 };
    persist({ result: upsertItem(base, editing), status: status === 'unsearched' ? 'searched' : status });
    setEditing(null);
  }

  var monsterName = deriveTabName(monster, index);

  return (
    <div className="vi-tr-modal" role="dialog" aria-label="Monster Treasure">
      <div className="vi-tr-modal-card">
        <div className="vi-tr-modal-head">
          <div>
            <div className="vi-tr-modal-eyebrow">Treasure</div>
            <div className="vi-tr-modal-title">{monsterName}</div>
            <div className="vi-tr-modal-sub">{mctx.creatureType} &middot; level {mctx.level} &middot; <span className={'vi-tr-status vi-tr-status-' + status}>{status.toUpperCase()}</span></div>
          </div>
          <button type="button" className="vi-tr-close" onClick={props.onClose}>&times;</button>
        </div>

        <div className="vi-tr-modal-body">
          {!ready ? (
            <div className="vi-tr-empty">{cat.loading ? 'Loading treasure catalogue...' : ('Treasure catalogue unavailable' + (cat.error ? (': ' + cat.error) : '') + '.')}</div>
          ) : (
            <React.Fragment>
              <div className="vi-tr-chiprow">
                <span className="vi-tr-chiplabel">Wealth</span>
                {WEALTH_TIERS.map(function (w) { return <button key={w} type="button" className={'vi-tr-chip' + (wealth === w ? ' is-on' : '')} onClick={function () { setWealth(w); }}>{w}</button>; })}
              </div>
              <div className="vi-tr-chiprow">
                <span className="vi-tr-chiplabel">Quantity</span>
                {QUANT.map(function (q) { return <button key={q} type="button" className={'vi-tr-chip' + (quantity === q ? ' is-on' : '')} onClick={function () { setQuantity(q); }}>{q}</button>; })}
              </div>

              <div className="vi-tr-actionbar">
                <button type="button" className="vi-tr-primary" onClick={doGenerate}>{result ? '↻ Reroll All' : '⚒ Generate Treasure'}</button>
                {result ? <button type="button" className="vi-tr-btn" onClick={onAddNew}>+ Add Item</button> : null}
                {result ? <button type="button" className="vi-tr-btn" onClick={function () { ctx.updateMonsterTreasureStatus(index, status === 'taken' ? 'searched' : 'taken'); }}>{status === 'taken' ? 'Unmark Taken' : '✓ Loot Taken'}</button> : null}
                {result ? <button type="button" className="vi-tr-btn vi-tr-btn-danger" onClick={function () { ctx.clearMonsterTreasure(index); setEditing(null); }}>Clear</button> : null}
              </div>

              {editing ? (
                <div className="vi-tr-editform">
                  <input className="vi-tr-input" placeholder="Item name" value={editing.name} onChange={function (e) { setEditing(Object.assign({}, editing, { name: e.target.value })); }} />
                  <select className="vi-tr-input" value={editing.category} onChange={function (e) { setEditing(Object.assign({}, editing, { category: e.target.value })); }}>
                    {CATEGORIES.filter(function (c) { return c !== 'coins'; }).map(function (c) { return <option key={c} value={c}>{categoryLabel(c)}</option>; })}
                  </select>
                  <input className="vi-tr-input vi-tr-input-sm" type="number" min="1" value={editing.qty} onChange={function (e) { setEditing(Object.assign({}, editing, { qty: e.target.value })); }} />
                  <input className="vi-tr-input vi-tr-input-sm" type="number" min="0" placeholder="value" value={editing.unitValue} onChange={function (e) { setEditing(Object.assign({}, editing, { unitValue: e.target.value })); }} />
                  <button type="button" className="vi-tr-primary" onClick={submitEdit}>Save</button>
                  <button type="button" className="vi-tr-btn" onClick={function () { setEditing(null); }}>Cancel</button>
                </div>
              ) : null}

              {result ? (
                <TreasureResult result={result} currency={catalogue.currency} actions={{ onRemoveItem: onRemoveItem, onRerollItem: onRerollItem, onEditItem: onEditItem }} />
              ) : (
                <div className="vi-tr-empty">This monster is <strong>unsearched</strong>. Generate treasure to search it. The result stays with this monster and will not change unless you reroll.</div>
              )}
            </React.Fragment>
          )}
        </div>
      </div>
      <div className="vi-tr-modal-scrim" onClick={props.onClose}></div>
    </div>
  );
}

export default MonsterTreasureModal;
